import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import useWindowFocus from '../../hooks/useWindowFocus';
import InvoicePreview from './InvoicePreview';

const RecentOrdersWidget = ({ token }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [settleOrder, setSettleOrder] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleSettleOrder = async (orderId, pMode) => {
        setSaving(true);
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=settle_order', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ order_id: orderId, payment_mode: pMode })
            });
            const json = await res.json();
            if (json.success) {
                setSettleOrder(null);
                fetchOrders();
            } else {
                alert(json.message);
            }
        } catch (err) {
            console.error("Settle Order Error:", err);
            alert("Failed to settle order.");
        } finally {
            setSaving(false);
        }
    };

    const fetchOrders = useCallback(async () => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=fetch_orders', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ limit: 5 })
            });
            if (res.status === 401) {
                // Let the parent (AdminDashboard) handle the logout redirect
                return;
            }
            const json = await res.json();
            if (json.success) setOrders(json.orders.slice(0, 5));
        } catch (err) {
            console.error("Fetch Recent Orders Widget Error:", err);
            if (err.name === 'TypeError' && err.message.includes('fetch')) {
                setTimeout(() => fetchOrders(), 5000);
            }
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (!token) return;
        
        fetchOrders();
        
        const intervalId = setInterval(() => {
            fetchOrders();
        }, 30000);
        
        return () => clearInterval(intervalId);
    }, [token, fetchOrders]);

    useWindowFocus(() => {
        if (token) fetchOrders();
    });

    if (loading) return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', marginBottom: '28px', textAlign: 'center', color: '#94a3b8' }}>
            Loading recent orders...
        </div>
    );

    return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', marginBottom: '28px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: '800', color: '#1e293b' }}>
                    <i className="fas fa-shopping-bag" style={{ marginRight: '8px', color: 'var(--primary-orange)' }} /> Recent Software Orders
                </h3>
                <Link to="pos" style={{ fontSize: '0.85em', color: 'var(--primary-orange)', fontWeight: '700', textDecoration: 'none' }}>View All History &rarr;</Link>
            </div>

            {orders.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {orders.map(ord => (
                        <div key={ord.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 15px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '700', fontSize: '0.9em', color: '#1e293b' }}>{ord.order_no}</div>
                                <div style={{ fontSize: '0.8em', color: '#64748b' }}>
                                    {ord.table_no || ord.customer_name || 'Guest'} • {new Date(ord.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                <span style={{ fontSize: '0.7em', padding: '4px 10px', borderRadius: '50px', background: ord.order_status === 'Paid' ? '#dcfce7' : (ord.order_status === 'Awaiting Payment' ? '#fee2e2' : '#fef3c7'), color: ord.order_status === 'Paid' ? '#166534' : (ord.order_status === 'Awaiting Payment' ? '#991b1b' : '#b45309'), fontWeight: '800', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{ord.order_status}</span>
                                {ord.payment_txn_id && ord.order_status === 'Awaiting Payment' && (
                                    <span style={{ fontSize: '0.65em', padding: '4px 8px', borderRadius: '50px', background: '#e0f2fe', color: '#0369a1', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Claimed (UPI: {ord.payment_txn_id})</span>
                                )}
                                <span style={{ fontSize: '0.7em', padding: '4px 10px', borderRadius: '50px', background: '#e0f2fe', color: '#0369a1', fontWeight: '800', textTransform: 'uppercase' }}>KOT: {ord.kitchen_status || 'Pending'}</span>
                                {ord.order_type === 'Delivery' && (
                                    <span style={{ fontSize: '0.7em', padding: '4px 10px', borderRadius: '50px', background: ord.delivery_status === 'Delivered' ? '#dcfce7' : ord.delivery_status === 'Out for Delivery' ? '#fef3c7' : '#f1f5f9', color: ord.delivery_status === 'Delivered' ? '#166534' : ord.delivery_status === 'Out for Delivery' ? '#b45309' : '#475569', fontWeight: '800', textTransform: 'uppercase' }}>
                                        <i className="fas fa-motorcycle" style={{ marginRight: '4px' }}></i>
                                        {ord.delivery_status || 'Pending Dispatch'}
                                    </span>
                                )}
                                {(ord.order_status === 'Pending' || ord.order_status === 'Awaiting Payment') && (
                                    <button 
                                        onClick={() => setSettleOrder(ord)} 
                                        style={{ background: 'var(--primary-orange)', border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.8em', boxShadow: '0 2px 4px rgba(255,102,0,0.2)' }}
                                    >
                                        {ord.order_status === 'Awaiting Payment' ? 'Settle & KOT' : 'Settle'}
                                    </button>
                                )}
                                {ord.customer_phone && (
                                    <a 
                                        href={`https://wa.me/${ord.customer_phone.replace(/\D/g, '')}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        style={{ background: '#25D366', color: '#fff', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontSize: '0.9em', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                                    >
                                        <i className="fab fa-whatsapp" />
                                    </a>
                                )}
                                <button 
                                    onClick={() => setSelectedOrder(ord.id)} 
                                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9em', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', transition: 'all 0.2s' }}
                                    title="View Bill"
                                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--primary-orange)'; e.currentTarget.style.borderColor = 'var(--primary-orange)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                                >
                                    <i className="fas fa-file-invoice" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '0.9em' }}>
                    <i className="fas fa-inbox" style={{ fontSize: '2rem', marginBottom: '10px', display: 'block', opacity: 0.2 }} />
                    No orders yet. They will appear here once customers place orders from your menu.
                </div>
            )}

            {settleOrder && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                    <div style={{ background: '#fff', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '380px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ marginTop: 0 }}>Settle Payment</h3>
                        <p style={{ color: '#64748b', fontSize: '0.9em', marginBottom: '20px' }}>
                            Collect payment for <b>{settleOrder.order_no}</b><br/>
                            Total Due: <b style={{ fontSize: '1.2em', color: '#000' }}>₹{settleOrder.grand_total}</b>
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                            {['Cash', 'Card', 'UPI'].map(mode => (
                                <button 
                                    key={mode}
                                    disabled={saving}
                                    onClick={() => handleSettleOrder(settleOrder.id, mode)}
                                    style={{ padding: '15px 5px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontWeight: '700' }}
                                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary-orange)'}
                                    onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                                >
                                    {mode}
                                </button>
                            ))}
                        </div>
                        <button 
                            onClick={() => setSettleOrder(null)}
                            style={{ width: '100%', padding: '10px', border: 'none', background: 'none', color: '#64748b', fontWeight: '600', cursor: 'pointer' }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {selectedOrder && (
                <InvoicePreview orderId={selectedOrder} token={token} onClose={() => setSelectedOrder(null)} />
            )}
        </div>
    );
};

export default RecentOrdersWidget;
